import axios from 'axios';

const API_BASE = 'https://khfsazxate.eu-central-1.awsapprunner.com/api';

// Axios interceptor: attach JWT token to every request if present
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Evita di mostrare più volte il messaggio se molte richieste ritornano 401
// insieme (es. salvataggio di 26 righe variante sul prodotto 84).
let sessionExpiredHandled = false;

// Handle 401 (token expired/invalid) globally
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Token is invalid or expired
      const currentPath = window.location.pathname;
      if (currentPath.startsWith('/admin') && !currentPath.includes('login')) {
        if (!sessionExpiredHandled) {
          sessionExpiredHandled = true;
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          // Messaggio CHIARO invece del fallimento silenzioso: l'alert è
          // bloccante, quindi i dati del form restano a video finché
          // l'operatore non conferma. Notifica anche eventuali listener.
          try {
            window.dispatchEvent(new CustomEvent('admin:session-expired'));
          } catch (e) { /* CustomEvent non disponibile: ignora */ }
          // eslint-disable-next-line no-alert
          window.alert('Sessione scaduta. Per sicurezza devi effettuare di nuovo il login. Le modifiche non ancora salvate potrebbero andare perse: annota ciò che ti serve prima di continuare.');
          window.location.href = '/admin';
        }
      }
    }
    return Promise.reject(error);
  }
);

const api = {
  // Products
  getProducts: () => axios.get(`${API_BASE}/products.php`),
  getProduct: (slug) => axios.get(`${API_BASE}/products.php?slug=${slug}`),
  getProductsByCategory: (category) => axios.get(`${API_BASE}/products.php?category=${category}`),
  getFeaturedProducts: () => axios.get(`${API_BASE}/products.php?featured=1`),
  searchProducts: (query) => axios.get(`${API_BASE}/products.php?search=${query}`),
  createProduct: (data) => axios.post(`${API_BASE}/products.php`, data),
  updateProduct: (data) => axios.put(`${API_BASE}/products.php`, data),
  deleteProduct: (id) => axios.delete(`${API_BASE}/products.php`, { data: { id } }),

  // Categories
  getCategories: () => axios.get(`${API_BASE}/categories.php`),
  getCategoriesTree: () => axios.get(`${API_BASE}/categories.php?tree=1`),
  getCategory: (slug) => axios.get(`${API_BASE}/categories.php?slug=${slug}`),
  createCategory: (data) => axios.post(`${API_BASE}/categories.php`, data),
  updateCategory: (data) => axios.put(`${API_BASE}/categories.php`, data),
  deleteCategory: (id) => axios.delete(`${API_BASE}/categories.php`, { data: { id } }),

  // Foods (Materials)
  getFoods: () => axios.get(`${API_BASE}/foods.php`),
  getFood: (slug) => axios.get(`${API_BASE}/foods.php?slug=${slug}`),
  getFoodsByCategory: (category) => axios.get(`${API_BASE}/foods.php?category=${category}`),
  getFoodsByLetter: (letter) => axios.get(`${API_BASE}/foods.php?letter=${letter}`),
  searchFoods: (query) => axios.get(`${API_BASE}/foods.php?search=${query}`),
  createFood: (data) => axios.post(`${API_BASE}/foods.php`, data),
  updateFood: (data) => axios.put(`${API_BASE}/foods.php`, data),
  deleteFood: (id) => axios.delete(`${API_BASE}/foods.php`, { data: { id } }),

  // News
  getNews: () => axios.get(`${API_BASE}/news.php`),
  getAllNews: () => axios.get(`${API_BASE}/news.php?all=1`),
  getNewsItem: (slug) => axios.get(`${API_BASE}/news.php?slug=${slug}`),
  createNews: (data) => axios.post(`${API_BASE}/news.php`, data),
  updateNews: (data) => axios.put(`${API_BASE}/news.php`, data),
  deleteNews: (id) => axios.delete(`${API_BASE}/news.php`, { data: { id } }),

  // FAQs
  getFaqs: () => axios.get(`${API_BASE}/faqs.php`),
  searchFaqs: (query) => axios.get(`${API_BASE}/faqs.php?search=${query}`),
  createFaq: (data) => axios.post(`${API_BASE}/faqs.php`, data),
  updateFaq: (data) => axios.put(`${API_BASE}/faqs.php`, data),
  deleteFaq: (id) => axios.delete(`${API_BASE}/faqs.php`, { data: { id } }),

  // Quotes
  submitQuote: (data) => axios.post(`${API_BASE}/quotes.php`, data),
  getQuotes: () => axios.get(`${API_BASE}/quotes.php`),
  getQuote: (id) => axios.get(`${API_BASE}/quotes.php?id=${id}`),
  updateQuoteStatus: (data) => axios.put(`${API_BASE}/quotes.php`, data),

  // Auth
  login: (credentials) => axios.post(`${API_BASE}/auth.php`, credentials),

  // Upload
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return axios.post(`${API_BASE}/upload.php`, formData);
  },

  // Featured products (homepage section config)
  getFeaturedConfig: () => axios.get(`${API_BASE}/featured.php`),
  updateFeaturedConfig: (data) => axios.put(`${API_BASE}/featured.php`, data),

  // Catalog
  getCatalog: () => axios.get(`${API_BASE}/catalog.php`),
  uploadCatalog: (file, onProgress) => {
    const formData = new FormData();
    formData.append('catalog', file);
    return axios.post(`${API_BASE}/catalog.php`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
  },

  // API Keys (admin only)
  getApiKeys: () => axios.get(`${API_BASE}/api-keys.php`),
  createApiKey: (data) => axios.post(`${API_BASE}/api-keys.php`, data),
  updateApiKey: (data) => axios.put(`${API_BASE}/api-keys.php`, data),
  deleteApiKey: (id) => axios.delete(`${API_BASE}/api-keys.php`, { data: { id } }),

  // Analytics (admin only)
  getAnalytics: (section = 'overview', period = '7d') =>
    axios.get(`${API_BASE}/analytics.php?section=${section}&period=${period}`),

  // Product Variants
  getVariants: (productId) => axios.get(`${API_BASE}/variants.php?product_id=${productId}`),
  createVariant: (data) => axios.post(`${API_BASE}/variants.php`, data),
  updateVariant: (id, data) => axios.put(`${API_BASE}/variants.php?id=${id}`, data),
  deleteVariant: (id) => axios.delete(`${API_BASE}/variants.php?id=${id}`),
  reorderVariants: (productId, order) => axios.post(`${API_BASE}/variants.php?action=reorder`, { product_id: productId, order }),

  // Quote notification settings (admin only)
  getNotifySettings: () => axios.get(`${API_BASE}/notify-settings.php`),
  updateNotifySettings: (data) => axios.put(`${API_BASE}/notify-settings.php`, data),
  sendNotifyTest: (data) => axios.post(`${API_BASE}/notify-settings.php`, { action: 'test', ...data }),
};

export default api;
